import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material';
import { injectIntl } from 'react-intl';
import { sanitize } from 'dompurify';
import { CircleHelp, Gem, Import, Search, SlidersHorizontal, Users, X } from 'lucide-react';
import { clientBaseBandOf } from '../clientPresentation';
import type { IntlLike } from '../clientPresentation';

export type ClientCommandBarProps = {
  searchTerm: string;
  onSearch: (term: string) => void;
  filterOpen: boolean;
  onFilter: () => void;
  onFilterClose: () => void;
  onPotentialClients?: () => void;
  onImportClients?: () => void;
  onAiSelect?: () => void;
  potentialCount?: number;
  /**
   * Adviser-level client-base band (0 high / 1 mid / 2 low), null when the
   * server sent none. It rode the tab row below until it was taking width the
   * tabs needed on a phone; it is a standing fact about the adviser's own book
   * rather than a control over this list, so it sits ahead of the search box —
   * read on the way in, never in the way.
   */
  clientBaseLevel?: number | null;
  onClientBase?: () => void;
  /**
   * The calendar view. The grid plots every client with a date on it, so the
   * search box and the filter would not narrow what is on screen. They stay in
   * place — the bar does not jump, and C.P.I. / 潛在客戶 / 匯入 / A.I. keep
   * working — but switched off, saying why. The search term is kept, not
   * cleared, and comes back on 列表.
   */
  searchDisabled?: boolean;
  intl: IntlLike;
};

const badgeOf = (count: number | undefined): string => (count && count > 99 ? '99+' : String(count || ''));

const ClientCommandBar = ({
  searchTerm,
  onSearch,
  filterOpen,
  onFilter,
  onFilterClose,
  onPotentialClients,
  onImportClients,
  onAiSelect,
  potentialCount,
  clientBaseLevel,
  onClientBase,
  searchDisabled = false,
  intl
}: ClientCommandBarProps) => {
  const [powerSearchOpen, setPowerSearchOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement | null>(null);
  const filterWasOpenRef = useRef(filterOpen);
  const message = (id: string) => intl.formatMessage({ id });
  const html = (id: string) => ({ __html: sanitize(message(id)) });
  const searchPlaceholder = message('clientv2-search-placeholder');
  const band = clientBaseBandOf(clientBaseLevel, intl);
  const helpLabel = message('clientv2-power-search-help');
  const calendarOffLabel = message('clientv2-calendar-search-off');

  useEffect(() => {
    if (filterWasOpenRef.current && !filterOpen) filterButtonRef.current?.focus();
    filterWasOpenRef.current = filterOpen;
  }, [filterOpen]);

  return (
    <>
      <nav className="clientv2-command-bar" aria-label={message('clientv2-command-label')}>
        {/* The adviser's own client-base band, as narrow as a fact can be drawn:
            a short bar in the band's colour — V1's own shape for this — and the
            band's word. The label 客戶群 that used to precede it is the button's
            accessible name and its tooltip instead; beside a search box, a
            coloured bar next to 「高」 is already the whole sentence, and the
            explainer it opens says the rest.
            On a phone the word goes too, leaving the bar alone: the command bar
            wraps at that width, and this chip was what pushed it onto a second
            line. What it means is still one tap (and one screen-reader label)
            away, and the colour is the part that was doing the work anyway. */}
        <button
          type="button"
          data-testid="client-base-indicator"
          className="clientv2-basechip"
          style={{ borderColor: band.color }}
          aria-label={`${message('clientv2-client-base')} · ${band.wording}`}
          title={`${message('Client_Potential_Index')} · ${band.wording}`}
          onClick={onClientBase}
        >
          <span
            className="clientv2-basechip-bar"
            data-testid="client-base-bar"
            style={{ backgroundColor: band.color }}
            aria-hidden="true"
          />
          <span className="clientv2-basechip-word">{band.wording}</span>
        </button>

        <div
          className={`clientv2-command-search${searchDisabled ? ' is-disabled' : ''}`}
          title={searchDisabled ? calendarOffLabel : undefined}
        >
          <Search size={17} aria-hidden="true" />
          <input
            type="search"
            data-testid="client-search"
            // Empty while switched off: a kept term showing in a dead box would
            // read as a search the calendar is obeying.
            value={searchDisabled ? '' : searchTerm}
            aria-label={searchPlaceholder}
            placeholder={searchDisabled ? calendarOffLabel : searchPlaceholder}
            disabled={searchDisabled}
            onChange={event => onSearch(event.target.value)}
          />
          {searchTerm && !searchDisabled && (
            <button
              type="button"
              className="clientv2-search-icon-button"
              aria-label={message('clientv2-search-clear')}
              onClick={() => onSearch('')}
            >
              <X size={15} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            className="clientv2-search-icon-button"
            aria-label={helpLabel}
            title={helpLabel}
            disabled={searchDisabled}
            onClick={() => setPowerSearchOpen(true)}
          >
            <CircleHelp size={16} aria-hidden="true" />
          </button>
        </div>

        <button
          type="button"
          ref={filterButtonRef}
          className="clientv2-command-button clientv2-command-filter"
          aria-label={message('clientv2-filter-toggle')}
          title={searchDisabled ? message('clientv2-calendar-filter-off') : message('clientv2-filter-toggle')}
          aria-expanded={filterOpen}
          aria-controls="clientv2-smart-filter"
          disabled={searchDisabled}
          onClick={onFilter}
        >
          <SlidersHorizontal size={16} aria-hidden="true" />
          <span>{message('clientv2-filter-toggle')}</span>
        </button>

        {/* 回到舊版, 重新整理, 通知, 說明 and 語音 are NOT here. 回到舊版 lives in
            the top navigation bar, mirroring where V1 puts 試用新版客戶頁
            (clientPage.js:2121); 通知/說明 join it there too, V1-style
            (clientPage.js:2107-2130) — ClientV2Bridge portals all three.
            重新整理 sits in ClientTabs, folded into the tab/sort row. 語音 moved
            into SmartFilterSidebar — it fills in the same filters below it, so
            it belongs beside them, not in the icon row above. */}

        <span className="clientv2-command-grow" />

        <button
          type="button"
          className="clientv2-command-button"
          aria-label={message('clientv2-command-potential')}
          title={message('clientv2-command-potential')}
          onClick={onPotentialClients}
        >
          <Users size={16} aria-hidden="true" />
          <span>{message('clientv2-command-potential')}</span>
          {!!potentialCount && <span className="clientv2-command-badge">{badgeOf(potentialCount)}</span>}
        </button>
        {/* `is-import`: the one control in this row that brings clients INTO
            the book, and the one an adviser looking at a thin list actually
            needs to find. It was the third identical white pill in a row of
            them, which is where a thing gets looked past. */}
        <button
          type="button"
          className="clientv2-command-button is-import"
          aria-label={message('clientv2-command-import')}
          title={message('clientv2-command-import')}
          onClick={onImportClients}
        >
          <Import size={16} aria-hidden="true" />
          <span>{message('clientv2-command-import')}</span>
        </button>

        <button
          type="button"
          className="clientv2-command-button is-ai"
          aria-label={message('clientv2-command-ai')}
          title={message('clientv2-command-ai')}
          onClick={onAiSelect}
        >
          <Gem size={16} aria-hidden="true" />
          <span>{message('clientv2-command-ai')}</span>
        </button>
        {/* No ToDo button here: `topNavigation`'s TodoIconButton is rendered on
            every page including this one, so a second one would be a duplicate. */}
      </nav>
      <button
        type="button"
        data-testid="filter-scrim"
        className={`clientv2-filter-scrim${filterOpen ? ' is-open' : ''}`}
        aria-label={message('clientv2-filter-close')}
        onClick={onFilterClose}
      />

      <Dialog
        open={powerSearchOpen}
        onClose={() => setPowerSearchOpen(false)}
        fullWidth
        aria-labelledby="clientv2-power-search-title"
      >
        <DialogTitle id="clientv2-power-search-title">{message('Smart Search')}</DialogTitle>
        <DialogContent className="clientv2-power-search-dialog">
          <p>{message('Basic search based on following items in FACTSHEET.')}</p>
          <p dangerouslySetInnerHTML={html('power_search_include')} />
          <p dangerouslySetInnerHTML={html('power_search_example')} />
          <p dangerouslySetInnerHTML={html('power_search_more_accurate')} />
          <ul>
            {[
              'power_search_phone',
              'power_search_more',
              'power_search_less',
              'power_search_tag',
              'clientv2-power-search-family',
              'power_search_age',
              'power_search_bday',
              'power_search_disc',
              'power_search_occupation',
              'power_search_referrer'
            ].map(id => (
              <li key={id} dangerouslySetInnerHTML={html(id)} />
            ))}
          </ul>
          <p dangerouslySetInnerHTML={html('power_search_eg')} />
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setPowerSearchOpen(false)}>
            {message('OK')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default injectIntl(ClientCommandBar);
